import { AuthStatus } from '../AuthStatus';
import { routes } from '../../lib/assessment/routes';
import { AccountAffordance } from './AccountAffordance';
import { AppNav } from './AppNav';
import { BrandMark } from './BrandMark';
import { DesktopNav } from './DesktopNav';
import { MobileNav } from './MobileNav';

/**
 * The shared header (specification 3.1–3.7).
 *
 * Both shells render the same brand mark and the same account contract; only
 * the Experience Shell carries the marketing navigation, because the
 * Application Shell's routes are reached from inside the product rather than
 * from a site menu.
 *
 * The two variants differ in one further respect, and it is deliberate. The
 * Application Shell keeps `AuthStatus`, whose signed-out branch renders the
 * sign-in form inline — that is the delivered behaviour and this slice does not
 * change it. The Experience Shell uses the compact `AccountAffordance`, because
 * section 3.5 requires sign-in to be tertiary there and a form in the header of
 * a marketing page competes with the page's primary action.
 */
export type SiteHeaderProps = {
  variant: 'public' | 'app';
};

export function SiteHeader({ variant }: SiteHeaderProps) {
  if (variant === 'app') {
    return (
      <header
        data-testid="site-header"
        data-shell="app"
        className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4"
      >
        <BrandMark />
        {/* U1a. `AppNav` decides for itself whether to render — signed-in only,
            and never on the assessment runner — so the header states the layout
            and holds no auth or route logic of its own. `flex-wrap` lets the nav
            drop to its own line on narrow viewports instead of crushing the
            brand mark and the auth control. */}
        <AppNav />
        {/* An entry point: signing in from the application's own header is not
            a gate in front of anything, so it lands on the dashboard. The gates
            — the report, workbook, result, and library pages — pass no
            `redirectTo` and keep the visitor on what they opened. */}
        <AuthStatus redirectTo={routes.dashboard} />
      </header>
    );
  }

  return (
    // `relative` anchors the mobile panel, which spans the header's full width
    // rather than the toggle's.
    <header data-testid="site-header" data-shell="public" className="relative border-b border-border">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4 md:px-8 py-5">
        <BrandMark />
        <div className="flex items-center gap-6">
          <DesktopNav />
          <div className="hidden md:block">
            <AccountAffordance idPrefix="desktop-account" layout="disclosure" />
          </div>
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
